import {
  AgxSpacer,
  AgxTooltip,
  AgxTypography,
  Icon_CheckmarkOutline,
} from '@urbanx/agx.ui-components2';
import { MouseEvent, ReactNode, useRef, useState } from 'react';

interface CopyableContactValueProps {
  id: string;
  value: string;
  href: string;
  icon: ReactNode;
}

const CopyableContactValue = ({ id, value, href, icon }: CopyableContactValueProps) => {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const copyValue = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    await navigator.clipboard.writeText(value);
    setCopied(true);
  };

  const keepCopiedState = () => clearTimeout(resetTimer.current);
  const resetCopiedState = () => {
    resetTimer.current = setTimeout(() => setCopied(false), 200);
  };

  return (
    <AgxSpacer
      className="campaignOverviewCopyableValue"
      direction="horizontal"
      contentAlignment="center"
      gap="spacing-xs"
      onMouseEnter={keepCopiedState}
      onMouseLeave={resetCopiedState}
      style={{ position: 'relative', minWidth: 0, width: 'fit-content' }}
    >
      <AgxSpacer
        direction="horizontal"
        contentAlignment="center"
        contentJustification="center"
        flexShrink={0}
        style={{ width: 14, transform: 'translateY(1px)' }}
      >
        {icon}
      </AgxSpacer>
      <a href={href} className="campaignOverviewLink" onClick={(event) => event.stopPropagation()}>
        <AgxTypography id={id} type="hyperlink" size="small" color="var(--text-tertiary)">
          {value}
        </AgxTypography>
      </a>
      <AgxTooltip
        id={`${id}-copy`}
        className="campaignOverviewCopyTooltip"
        contentClassName="campaignOverviewCopyTooltipContent"
        content={copied ? 'Copied' : 'Copy'}
        placement="top"
      >
        <button
          type="button"
          className="campaignOverviewCopyButton"
          aria-label={`${copied ? 'Copied' : 'Copy'} ${value}`}
          onClick={copyValue}
        >
          {copied ? (
            <Icon_CheckmarkOutline width={21} height={21} color="var(--text-success)" />
          ) : (
            <CopyIcon />
          )}
        </button>
      </AgxTooltip>
    </AgxSpacer>
  );
};

function CopyIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect
        x="5.25"
        y="5.25"
        width="7.5"
        height="7.5"
        rx="1.25"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M10.75 5V4.25C10.75 3.56 10.19 3 9.5 3H4.25C3.56 3 3 3.56 3 4.25V9.5C3 10.19 3.56 10.75 4.25 10.75H5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default CopyableContactValue;
